import { useIntl } from "react-intl"
import { useEffect, useRef, useState } from "react"
import React from "react"
import ProfessionalActivityTable from "./ProfessionalActivityTable"
import DisposablesTable from "./DisposablesTable"
import EquipmentTable from "./EquipmentTable"
import NutritionalSupportTable from "./NutritionalSupportTable"
import MedicinesTable from "./MedicinesTable"
import ExtrasTable from "./ExtrasTable"
import { CustomTable, CustomTableRef, formatCurrency } from "medere-ui-framework"
import { Column } from "primereact/column"
import { BudgetDTO } from "../../../dto/budget/BudgetDTO"
import { MAX_DECIMAL } from "../../../interface/orderOfProvision"
import Big from "big.js"
import NvTooltips from "../../../components/novarium/NvTooltips"

interface BudgetTableProps {
    budget: BudgetDTO;
    setBudget?: (budget: BudgetDTO | ((prevBudget: BudgetDTO) => BudgetDTO)) => void;
    defaultEarnings: number;
    date?: Date;
    selectedHealthEntity?: number;
    selectedHealthEntityPlan?: number;
    CRUDBehaviour?: boolean;
    showPriceByItem?: boolean;
    showSurcharge?: boolean;
    setShouldUpdateEquipmentPrices?: (value: boolean) => void;
}

export default function BudgetTable({budget, setBudget, defaultEarnings=0, CRUDBehaviour=true, showPriceByItem=true, showSurcharge=true, date, selectedHealthEntity, selectedHealthEntityPlan}:BudgetTableProps) {

    const intl = useIntl();
    const queryRef = useRef<CustomTableRef>(null);
    const [shouldUpdateEquipmentPrices, setShouldUpdateEquipmentPrices] = useState(false);

    // Inicializar el total cuando se cargan items existentes
    useEffect(() => {
        const calculatedTotal = calculateBudgetTotal(budget);
        // Solo actualizar si el total calculado es diferente al actual
        if (budget.total !== calculatedTotal) {
            setBudget?.((prevBudget: BudgetDTO) => ({
                ...prevBudget,
                total: calculatedTotal
            }));
        }
    }, [
        budget.professionalActivities.length,
        budget.disposables.length,
        budget.equipments.length,
        budget.nutritionalSupportItems.length,
        budget.medicines.length,
        budget.extras.length
    ]);

    useEffect(() => {
        queryRef.current?.refreshTable?.();
    }, [budget.total, budget.duration]);

    const addToTotal = (value: number) => {
        console.log("Viejo total del presupuesto:", budget.total);
        setBudget?.((prevBudget: BudgetDTO) => ({
            ...prevBudget,
            total: (prevBudget.total ?? 0) + value
        }));

        console.log("Añadiendo al total del presupuesto:", value);
        console.log("Nuevo total del presupuesto:", (budget.total ?? 0) + value);
    }

    const calculateBudgetTotal = (budget: BudgetDTO): number => {
        return [
            budget.professionalActivities,
            budget.disposables,
            budget.equipments,
            budget.nutritionalSupportItems,
            budget.medicines,
            budget.extras
        ].reduce((total, items) => total + items.reduce((sum, item) => sum + (item.valuemonth || 0), 0), 0);
    };

    const getTotals = (_offSet: number, _pageSize: number, _params?: any) => {
        const total = calculateBudgetTotal(budget);
        return Promise.resolve([{
            valueday: budget.duration ? total / budget.duration : 0,
            valuemonth: total
        }]);
    };

    const handleUpdatePrices = () => {
        setShouldUpdateEquipmentPrices(true);
    };

    const handleNutritionalSupportItemsChange = (items: BudgetDTO["nutritionalSupportItems"]) => {
        setBudget?.((prevBudget: BudgetDTO) => ({
            ...prevBudget,
            nutritionalSupportItems: items
        }));
    };

    const handleMedicinesChange = (items: BudgetDTO["medicines"]) => {
        setBudget?.((prevBudget: BudgetDTO) => ({
            ...prevBudget,
            medicines: items
        }));
    };

    const tableColumns = React.useMemo(() => {
        const columns = [
            <Column key="totales" style={{width:'63%'}} header={"Totales"} className="column" body={<p className="text-left">Presupuesto</p>}/>
        ];

        if (budget.duration > 0) {
            columns.push(
                <Column key="valordia" style={CRUDBehaviour?{width:'18.5%'}:{width:'14.5%'}} header={<p className={`text-end ${CRUDBehaviour?'':''}`}>{"Valor día"}</p>} className={`column ${CRUDBehaviour?'':''}`} align="right"
                body={(rowData) => {
                    return <p className={`text-end ${CRUDBehaviour?'':''}`}>{formatCurrency(Number(Big(rowData.valueday).toFixed(MAX_DECIMAL)))}</p>;
                }}/>
            );
        }

        columns.push(
            <Column key="valortotal" style={CRUDBehaviour?{width:'18.5%'}:{width:'14.5%'}} header={<p className={`text-end ${CRUDBehaviour?'mr-14':''}`}>{"Valor total"}</p>} className={`column font-bold ${CRUDBehaviour?'mr-14':''} `} align="right"
            body={(rowData) => {
                return <p className={`text-end ${CRUDBehaviour?'mr-14':''}`}>{formatCurrency(Number(Big(rowData.valuemonth).toFixed(MAX_DECIMAL)))}</p>;
            }}/>
        );

        return columns;
    }, [budget.duration, CRUDBehaviour]);

    return (
        <React.Fragment>
            <NvTooltips />
            {(CRUDBehaviour || (budget.professionalActivities.length > 0)) && (
                <ProfessionalActivityTable items={budget.professionalActivities} addToTotal={addToTotal} defaultEarnings={defaultEarnings} duration={budget.duration ?? 0} CRUDBehaviour={CRUDBehaviour} showPriceByItem={showPriceByItem} showEarnings={showSurcharge} date={date} healthEntity={selectedHealthEntity} healthEntityPlan={selectedHealthEntityPlan} />
            )}

            {(CRUDBehaviour || (budget.disposables.length > 0)) && (
                <DisposablesTable items={budget.disposables} addToTotal={addToTotal} defaultEarnings={defaultEarnings} duration={budget.duration ?? 0} CRUDBehaviour={CRUDBehaviour} showPriceByItem={showPriceByItem} showEarnings={showSurcharge}/>
            )}

            {(CRUDBehaviour || (budget.equipments.length > 0)) && (
                <EquipmentTable agreementplan={budget.agreementplan ?? 0} items={budget.equipments} addToTotal={addToTotal} defaultEarnings={defaultEarnings} duration={budget.duration ?? 0} CRUDBehaviour={CRUDBehaviour} showPriceByItem={showPriceByItem} showEarnings={showSurcharge} date={date} healthEntity={selectedHealthEntity} healthEntityPlan={selectedHealthEntityPlan}/>
            )}

            {(CRUDBehaviour || (budget.nutritionalSupportItems.length > 0)) && (
                <NutritionalSupportTable items={budget.nutritionalSupportItems} onItemsChange={handleNutritionalSupportItemsChange} addToTotal={addToTotal} defaultEarnings={defaultEarnings} duration={budget.duration ?? 0} CRUDBehaviour={CRUDBehaviour} showPriceByItem={showPriceByItem} showEarnings={showSurcharge}/>
            )}

            {(CRUDBehaviour || (budget.medicines.length > 0)) && (
                <MedicinesTable items={budget.medicines} onItemsChange={handleMedicinesChange} addToTotal={addToTotal} defaultEarnings={defaultEarnings} duration={budget.duration ?? 0} CRUDBehaviour={CRUDBehaviour} showPriceByItem={showPriceByItem} showEarnings={showSurcharge}/>
            )}

            {(CRUDBehaviour || (budget.extras.length > 0)) && (
                <ExtrasTable items={budget.extras} addToTotal={addToTotal} defaultEarnings={defaultEarnings} duration={budget.duration ?? 0} CRUDBehaviour={CRUDBehaviour} showPriceByItem={showPriceByItem} showEarnings={showSurcharge}/>
            )}

            <CustomTable idTable="tablebudget" getItems={getTotals} className='totalsBudgetTable' notFlex  filterRow={false} ref={queryRef}>
                {tableColumns}
            </CustomTable>
        </React.Fragment>
    )
}
